import { CardContent, Grid } from '@mui/material';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { QueryActivityPagePeriod } from '../../../api/schema';
import { CardHeaderFlex } from '../../../component/cardHeaderFlex/CardHeaderFlex';
import LinearProgress from '../../../component/linearProgress/LinearProgress';
import { useIsRemediation } from '../../../hooks/useIsRemediation';
import useSearchParams from '../../../hooks/useSearchParams';
import { fetchActivitySummary, getActivitySummaryFetching, getActivitySummaryFor } from '../../../redux/slices/activity/activitySlice';
import { getLastRefresh } from '../../../redux/slices/app/appSlice';
import { TOTAL_SERIES, getUtilizationSeries, itemUtilizationHistory, itemUtilizationHistoryFetching } from '../../../redux/slices/tools/toolsSlice';
import { getMarketsList } from '../../../redux/slices/verticalMarkets/verticalMarketsSlice';
import { useDispatch } from '../../../redux/store';
import { CardStyled } from '../../../styled/components';
import { viewerTimeZone } from '../../dashboard/kpiWindow';
import { TREND_PERIODS } from '../../dashboard/periodChart';
import ActivityBarChart, { ActivityBar } from './ActivityBarChart';
import ActivityControls from './ActivityControls';
import ActivityPeriodTable from './ActivityPeriodTable';
import ActivitySnapshot from './ActivitySnapshot';
import UtilizationMixCard from './UtilizationMixCard';
import { BOOMERANG_SERIES, EQUIPMENT_SERIES, JOB_SITE_SERIES } from './activityColors';
import {
  ActivityMode,
  ActivityPeriod,
  PERIOD_GRAIN,
  detailPath,
  formatDays,
  metricsOf,
  parseMode,
  parsePeriod,
  periodLabel,
  windowCaption,
} from './activityWindow';

/**
 * Activity: job-site events and equipment movement in one place, over a window
 * the reader picks.
 *
 * The two series answer different questions about the same period. Job counts
 * say how many sites were touched; equipment counts say how much work the
 * period carried -- and since billing is per piece out on a site regardless of
 * how long it stays, the equipment count is the one that tracks revenue. Ten
 * sites with two machines each is a lighter period than three with forty.
 */
// One height for all four charts, so the two rows line up whatever each card
// puts above its plot.
const CHART_HEIGHT = 190;

export const ToolsActivity = (): ReactElement => {
  const dispatch = useDispatch();
  const [params, setURLParams] = useSearchParams();
  const period = parsePeriod(params.period);
  const mode = parseMode(params.mode);
  const grain = PERIOD_GRAIN[period];
  const tz = useMemo(() => viewerTimeZone(), []);
  const lastRefresh = useSelector(getLastRefresh);
  const [showTrend, setShowTrend] = useState(true);
  const navigate = useNavigate();

  // The tab is hidden for other markets, but a bookmark is not.
  const isRemediation = useIsRemediation();
  const markets = useSelector(getMarketsList({}));

  const summary = useSelector(getActivitySummaryFor(period, mode));
  const fetching = useSelector(getActivitySummaryFetching);
  const utilization = useSelector(getUtilizationSeries);
  const utilizationFetching = useSelector(itemUtilizationHistoryFetching);

  useEffect(() => {
    dispatch(fetchActivitySummary({ period, mode, tz }));
  }, [dispatch, period, mode, tz, lastRefresh]);

  useEffect(() => {
    dispatch(itemUtilizationHistory());
  }, [dispatch, lastRefresh]);

  const periods = useMemo(() => summary?.periods || [], [summary]);
  const lead = useMemo(() => summary?.lead || [], [summary]);

  // Trailing average per series, starting at the window's FIRST period. The
  // server sends the periods immediately before the window for exactly this:
  // without them the line would have nothing behind its first points and would
  // start a third of the way into the chart.
  const trailingAverage = (lead: ActivityBar[], bars: ActivityBar[], seriesCount: number) => {
    const seeded = [...lead.slice(-(TREND_PERIODS - 1)), ...bars];
    const offset = seeded.length - bars.length;
    return Array.from({ length: seriesCount }, (_, series) =>
      bars.map((_bar, index) => {
        const end = index + offset;
        // Only skip a point when the lead itself is short, at the very start of
        // an organization's history.
        if (end < TREND_PERIODS - 1) return undefined;
        const slice = seeded.slice(end - TREND_PERIODS + 1, end + 1);
        return slice.reduce((sum, bar) => sum + bar.values[series], 0) / slice.length;
      }),
    );
  };

  const movementBars = useCallback(
    (rows: QueryActivityPagePeriod[]): ActivityBar[] =>
      rows.map((row) => {
        const m = metricsOf(row);
        return {
          key: row.date || '',
          label: periodLabel(row, grain, tz)
            .replace(/^Week of /, '')
            .replace(/ \(from .*\)$/, ''),
          title: periodLabel(row, grain, tz),
          values: [m.equipment_out, m.equipment_in],
          notes: [
            { name: 'Jobs opened', value: String(m.jobs_opened) },
            { name: 'Jobs closed', value: String(m.jobs_closed) },
            { name: 'Jobs expanded', value: String(m.jobs_expanded) },
            // Shown but not drawn: these are excluded from the bars above, and a
            // reader should be able to see that without opening the period.
            { name: 'Returns / Cancellations', value: `${m.boomerang_real} / ${m.boomerang_artifact}` },
          ],
        };
      }),
    [grain, tz],
  );

  const movement = useMemo(() => movementBars(periods), [movementBars, periods]);
  const movementLead = useMemo(() => movementBars(lead), [movementBars, lead]);

  const boomerangs = useMemo<ActivityBar[]>(
    () =>
      periods.map((row) => {
        const m = metricsOf(row);
        return {
          key: row.date || '',
          label: periodLabel(row, grain, tz)
            .replace(/^Week of /, '')
            .replace(/ \(from .*\)$/, ''),
          title: periodLabel(row, grain, tz),
          values: [m.boomerang_real, m.boomerang_artifact],
        };
      }),
    [periods, grain, tz],
  );

  // Job sites opened and closed. Counts, not rates: a job is a thing that
  // happened once, and "0.9 jobs a day" is not how anybody describes a week.
  // A period the window cuts short says so in its tooltip instead.
  const jobsBars = useCallback(
    (rows: QueryActivityPagePeriod[]): ActivityBar[] =>
      rows.map((row) => {
        const m = metricsOf(row);
        return {
          key: row.date || '',
          label: periodLabel(row, grain, tz)
            .replace(/^Week of /, '')
            .replace(/ \(from .*\)$/, ''),
          title: periodLabel(row, grain, tz),
          values: [m.jobs_opened, m.jobs_closed],
          notes: [{ name: 'Expanded', value: String(m.jobs_expanded) }, ...(row.partial ? [{ name: 'Partial period', value: formatDays(row.days || 0) }] : [])],
        };
      }),
    [grain, tz],
  );

  const jobs = useMemo(() => jobsBars(periods), [jobsBars, periods]);
  const jobsLead = useMemo(() => jobsBars(lead), [jobsBars, lead]);

  if (markets.result && !isRemediation) {
    return <Navigate to="/activity/dashboard/summary" replace />;
  }

  const caption = windowCaption(summary?.window?.from, summary?.window?.to, summary?.window?.days, tz);
  // A label under every period is unreadable once the axis gets busy; thin it
  // evenly rather than letting the labels collide.
  const labelEvery = periods.length > 16 ? 3 : periods.length > 10 ? 2 : 1;
  // A column opens its period, the same drill-down the table row gives.
  const openPeriod = (index: number) => {
    const row = periods[index];
    if (row) navigate(detailPath(row, grain, period, mode));
  };

  return (
    <>
      {fetching && <LinearProgress />}

      <ActivitySnapshot metrics={summary?.current} baseline={summary?.baseline?.average} days={summary?.window?.days} fetching={fetching} />

      <ActivityControls
        period={period}
        mode={mode}
        caption={caption}
        showTrend={showTrend}
        onPeriod={(value: ActivityPeriod) => setURLParams({ newParams: { period: value, mode } })}
        onMode={(value: ActivityMode) => setURLParams({ newParams: { period, mode: value } })}
        onShowTrend={setShowTrend}
      />

      {/* Four charts, two rows of two, every card the full height of its row
          so the tops and bottoms line up across the page. */}
      <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mb: 4 }} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <CardStyled sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex title="Equipment" />
            <CardContent sx={{ pt: 0, flexGrow: 1, '&:last-child': { pb: 2 } }}>
              <ActivityBarChart
                bars={movement}
                series={EQUIPMENT_SERIES}
                trend={showTrend ? trailingAverage(movementLead, movement, 2) : undefined}
                trendLabel={`${TREND_PERIODS}-${grain} avg`}
                fetching={fetching}
                height={CHART_HEIGHT}
                labelEvery={labelEvery}
                onSelect={openPeriod}
                formatValue={(value) => Math.round(value).toLocaleString()}
              />
            </CardContent>
          </CardStyled>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <UtilizationMixCard points={utilization.byCategory[TOTAL_SERIES] || []} from={summary?.window?.from} fetching={utilizationFetching} />
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <CardStyled sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex title="Job Sites" />
            <CardContent sx={{ pt: 0, flexGrow: 1, '&:last-child': { pb: 2 } }}>
              {/* Its own pair, not the equipment blues: a job opening is a
                  different kind of event from a machine moving, and the two
                  charts sit one above the other. */}
              <ActivityBarChart
                bars={jobs}
                series={JOB_SITE_SERIES}
                trend={showTrend ? trailingAverage(jobsLead, jobs, 2) : undefined}
                trendLabel={`${TREND_PERIODS}-${grain} avg`}
                fetching={fetching}
                height={CHART_HEIGHT}
                labelEvery={labelEvery}
                onSelect={openPeriod}
                emptyText="No job sites opened or closed in this window"
              />
            </CardContent>
          </CardStyled>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <CardStyled sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex title="Boomerangs" />
            <CardContent sx={{ pt: 0, flexGrow: 1, '&:last-child': { pb: 2 } }}>
              {/* No explanatory paragraph here: the legend already names both
                  classes, the drill-down spells them out, and a block of text
                  would push this plot out of line with the one beside it. */}
              <ActivityBarChart
                bars={boomerangs}
                series={BOOMERANG_SERIES}
                fetching={fetching}
                height={CHART_HEIGHT}
                labelEvery={3}
                onSelect={openPeriod}
                emptyText="No round trips in this window"
              />
            </CardContent>
          </CardStyled>
        </Grid>
      </Grid>

      <ActivityPeriodTable periods={periods} grain={grain} period={period} mode={mode} tz={tz} fetching={fetching} />
    </>
  );
};

export default ToolsActivity;
